// Subscription data model for the SaaS billing prototype.
// See plans/saas-billing/01-stripe-billing-foundation.md for canonical types.

import { AppliedCoupon } from './coupon.model';

export type SubscriptionStatus =
  | 'active'             // Healthy, paid, current
  | 'past_due'           // Latest charge failed, in retry window
  | 'unpaid'             // All retries exhausted, access being restricted
  | 'canceled'           // Cancelled, no further charges
  | 'incomplete'         // Initial payment failed (signup edge case); recoverable for 23h
  | 'incomplete_expired' // Stripe voided the initial invoice after 23h; terminal
  | 'paused';            // Reserved for future "pause subscription" feature

export type BillingPeriod = 'monthly' | 'annual';

// Provider amounts can use these currencies without opening new collection markets.
export const BILLING_CURRENCY_CODES = [
  'USD', 'NGN', 'GHS', 'KES', 'ZAR', 'GBP', 'EUR', 'ISK', 'UGX',
  'BIF', 'CLP', 'DJF', 'GNF', 'JPY', 'KMF', 'KRW', 'MGA', 'PYG',
  'RWF', 'VND', 'VUV', 'XAF', 'XOF', 'XPF',
] as const;
export type CurrencyCode = typeof BILLING_CURRENCY_CODES[number];

// CountryCode includes 'DE' | 'FR' | 'IE' to support EU reverse-charge demo
// fixtures in #10. EU markets are not yet open commercially; these are
// available for prototype scenarios only (not exposed in production signup).
export type CountryCode = 'US' | 'NG' | 'GH' | 'KE' | 'ZA' | 'GB' | 'DE' | 'FR' | 'IE';

export type TierCode = 'FOUNDATION' | 'PROFESSIONAL' | 'BUSINESS';

export interface Subscription {
  id: string;                          // Maps to TenantSubscription.Id
  stripeSubscriptionId: string | null; // Null until backend migration to real Stripe Subs
  tenantId: string;
  status: SubscriptionStatus;
  tier: TierCode;
  billingPeriod: BillingPeriod;
  currency: CurrencyCode;
  countryCode: CountryCode;
  seats: number;                       // Stripe Subscription Item quantity, min 5
  // Stage-1 NG flat pricing snapshot. Absent/undefined => 'per_seat'.
  pricingModel?: 'per_seat' | 'flat';
  includedSeats?: number | null;       // Flat only: seats bundled into the flat price
  extraSeatPriceMinor?: number | null; // Flat only: monthly extra-seat price, minor units
  pricePerSeatMinor: number;           // Per-seat: seat price. Flat: flat base price. Minor units
  subtotalMinor: number;               // per_seat: price*seats; flat: base + extra seats (pre-discount, pre-tax)
  discountMinor: number;               // From active Coupon, if any
  taxMinor: number;                    // Computed by Stripe Tax (0 until #10 ships)
  totalMinor: number;                  // subtotal - discount + tax
  appliedCoupon: AppliedCoupon | null;
  currentPeriodStart: string;          // ISO 8601
  currentPeriodEnd: string;            // ISO 8601 - next charge date
  cancelAtPeriodEnd: boolean;          // True if user cancelled but still in paid period
  canceledAt: string | null;
  pastDueSince: string | null;         // First failed payment timestamp
  gracePeriodEndsAt: string | null;    // When access is restricted (see #06)
}
